import type { components } from '../../lib/api/schema.gen'
import { deriveReadinessBucket } from './ship-as-ready-breakdown'
import type { ReadinessBucket } from './ship-as-ready-breakdown'

type LineItem = components['schemas']['LineItem']

/**
 * PO-group partitioning of a cart's READY lines (UID-97 K1 grouping, ADR-0044 §1),
 * moved into the cart vertical for UID-315 (ADR-0106 decision F). It is conceptually
 * readiness-bucket grouping — a cart-owned concept ({@link deriveReadinessBucket} in
 * `ship-as-ready-breakdown.ts`) — so it lives with the bucket primitive it depends on,
 * and both the cart split-PO card and the checkout placement re-import it from here
 * (checkout → cart is the only direction; there is no cart → checkout import, so the
 * dependency graph stays acyclic). Behaviour is unchanged from the prior
 * `checkout-domain.derivePoGroups`; `checkout-domain` re-exports these for its existing
 * call sites.
 */

/** A client-derived PO group, before the buyer has typed a PO number for it.
 * `key` is stable across re-renders (used as the `poNumbers` map key and the
 * React list key); `lineIds` are the cart READY-line `sourceable.id`s this
 * group will submit as one `OrderGroup`. */
export interface PoGroupDraft {
  readonly key: ReadinessBucket
  readonly label: string
  readonly lineIds: readonly string[]
}

const BUCKET_ORDER: readonly ReadinessBucket[] = ['standard', 'configurable', 'custom']

/** Human name for a bucket's PO-group label, e.g. "PO 1 · Standard parts"
 * (prototype `po.label` = `'PO '+(i+1)+' · '+g.name`, lines 2107-2115). */
const BUCKET_GROUP_NAME: Readonly<Record<ReadinessBucket, string>> = {
  standard: 'Standard parts',
  configurable: 'Configurable parts',
  custom: 'Custom parts',
}

/**
 * Partitions the cart's READY lines into PO groups for the split-PO toggle
 * (UID-97 K1, ADR-0044 §1). The prototype groups by cart "session" — a
 * client-only concept (an RFQ/workspace batch a set of lines was added
 * together from) that this app's cart model does not carry: `LineItem`s are
 * keyed by `sourceable.id` with no supplier/session attribute (verified,
 * ADR-0044 §1 "Alternatives considered"). Grouping by the readiness bucket
 * (`standard`/`configurable`/`custom` — the SAME UID-96 "Ship as ready"
 * concept already surfaced in the cart, `deriveReadinessBucket`) is the
 * closest existing, real grouping this app has, and it directly reuses that
 * seam rather than inventing a second one. This is a recorded FE scoping
 * decision, not a contract gap: the server does not care how the client
 * partitions — it only validates that the groups supplied partition EXACTLY
 * the ready lines (422 `order/invalid_grouping` otherwise).
 *
 * A single present bucket yields ONE group — the split toggle is only
 * offered when {@link derivePoGroups} returns more than one group (mirrors
 * the prototype's `cartGrouped = nGroups>1`, `Fictiv Platform.dc.html` line
 * ~7302).
 */
export function derivePoGroups(lines: readonly LineItem[]): readonly PoGroupDraft[] {
  const readyLines = lines.filter((line) => line.status === 'ready')
  const byBucket = new Map<ReadinessBucket, LineItem[]>()
  for (const line of readyLines) {
    const bucket = deriveReadinessBucket(line.sourceable.kind)
    const existing = byBucket.get(bucket)
    if (existing) {
      existing.push(line)
    } else {
      byBucket.set(bucket, [line])
    }
  }

  const presentBuckets = BUCKET_ORDER.filter((bucket) => (byBucket.get(bucket)?.length ?? 0) > 0)
  return presentBuckets.map((bucket, index) => ({
    key: bucket,
    label: `PO ${index + 1} · ${BUCKET_GROUP_NAME[bucket]}`,
    lineIds: (byBucket.get(bucket) ?? []).map((line) => line.sourceable.id),
  }))
}
